import { PageShell } from '@/components/layout/PageShell'
import { ConfiguredReportShell } from '../components/ConfiguredReportShell'
import {
  activePropertiesColumns,
  approvedNotPurchasedColumns,
  chargebackColumns,
  claimNotTimelyColumns,
  dailyApplicationColumns,
  dynamicPricingColumns,
  expireDefaultsColumns,
  failedLoginsColumns,
  feeNotPaidColumns,
  lossRatioColumns,
  lrForm16Columns,
  missingContactsColumns,
  mobileEventColumns,
  nonVerifiedColumns,
  pendingDefaultsColumns,
  propertyActivityColumns,
  w9Columns,
} from './columns'
import { display } from '../utils/configuredReportFormatters'
import { COMPANY_PROPERTY_SALES_FILTERS } from '../constants/configuredReportFilterOptions'
import {
  CHARGEBACK_REPORT_COLUMN_VISIBILITY_STORAGE_KEY,
  CHARGEBACK_REPORT_LOCKED_COLUMN_IDS,
  CHARGEBACK_REPORT_TRAILING_COLUMN_IDS,
  PENDING_REIMBURSEMENTS_COLUMN_VISIBILITY_STORAGE_KEY,
  PENDING_REIMBURSEMENTS_LOCKED_COLUMN_IDS,
} from '../constants/listColumns'
import reportStyles from '../styles/reportsTable.module.css'

export function NonVerifiedPropertyReportPage() {
  return (
    <ConfiguredReportShell
      config={{
        title: 'Non-verified Property Report',
        testIdPrefix: 'non-verified-property',
        routePath: 'non-verified-property',
        apiPath: '/api/pmc_verify',
        exportPath: '/api/export/pmc_verify',
        sessionKey: 'non_verified_property_filter',
        defaultOrderBy: 'company_name',
        search: true,
        selectFilters: [
          ...COMPANY_PROPERTY_SALES_FILTERS,
          {
            kind: 'paymentMethod',
            key: 'payment_method',
            label: 'Payment Method',
            placeholder: 'Payment Method',
          },
        ],
        textFilters: [
          {
            key: 'payee_user_name',
            label: 'Payee User Name',
            placeholder: 'Payee User Name',
          },
          { key: 'payment_address', label: 'Address', placeholder: 'Address' },
          { key: 'payment_city', label: 'City', placeholder: 'City' },
          { key: 'payment_state', label: 'State', placeholder: 'State' },
          { key: 'payment_zipcode', label: 'Zip', placeholder: 'Zip' },
        ],
        columns: nonVerifiedColumns,
      }}
    />
  )
}

export function PendingReimbursementsReportPage() {
  return (
    <ConfiguredReportShell
      config={{
        title: 'Pending Reimbursements Report',
        testIdPrefix: 'pending-defaults',
        routePath: 'pending-defaults',
        apiPath: '/api/residents_default',
        exportPath: '/api/export/default_report',
        sessionKey: 'pending_defaults_filter',
        defaultOrderBy: 'resident_name',
        search: true,
        selectFilters: [
          {
            kind: 'company',
            key: 'companyId',
            label: 'Company',
            placeholder: 'All Company',
          },
          {
            kind: 'property',
            key: 'propertyId',
            label: 'Property',
            placeholder: 'All Property',
          },
          {
            kind: 'rentalStatus',
            key: 'rentalStatus',
            label: 'Rental Status',
            placeholder: 'Rental Status',
          },
          {
            kind: 'salesTeam',
            key: 'sales_team_id',
            label: 'Sales Team',
            placeholder: 'Sales Team',
          },
          {
            kind: 'defaultStatus',
            key: 'defaultStatus',
            label: 'Default Status',
            placeholder: 'Default Status',
          },
          {
            kind: 'claimStatus',
            key: 'claim_status',
            label: 'Reimbursement Status',
            placeholder: 'All Reimbursement Statuses',
          },
          {
            kind: 'specialNote',
            key: 'special_note',
            label: 'Special Note',
            placeholder: 'Special Note',
          },
        ],
        columns: pendingDefaultsColumns,
        columnCustomization: {
          storageKey: PENDING_REIMBURSEMENTS_COLUMN_VISIBILITY_STORAGE_KEY,
          lockedColumnIds: [...PENDING_REIMBURSEMENTS_LOCKED_COLUMN_IDS],
        },
      }}
    />
  )
}

export function ClaimNotTimelyReportPage() {
  return (
    <ConfiguredReportShell
      config={{
        title: 'Reimbursement Not Opened Timely',
        testIdPrefix: 'claim-not-timely',
        routePath: 'claim-not-timely',
        apiPath: '/api/claim_not_timely',
        exportPath: '/api/export/claim_not_timely',
        sessionKey: 'claimnottimely_filter',
        defaultOrderBy: 'applicant_id',
        defaultSortBy: 'desc',
        search: true,
        dateFilter: {
          label: 'Lease Start Date',
          fromKey: 'lease_start_fromDt',
          toKey: 'lease_start_toDt',
        },
        selectFilters: [
          {
            kind: 'company',
            key: 'company_id',
            label: 'Company',
            placeholder: 'All Company',
          },
          {
            kind: 'property',
            key: 'property_id',
            label: 'Property',
            placeholder: 'All Property',
          },
        ],
        columns: claimNotTimelyColumns,
      }}
    />
  )
}

export function AutoExpireReimbursementsReportPage() {
  return (
    <ConfiguredReportShell
      config={{
        title: 'Auto Expire Reimbursements Report',
        testIdPrefix: 'expire-defaults',
        routePath: 'expire-defaults',
        apiPath: '/api/default_lock_report',
        exportPath: '/api/export/default_lock_report',
        sessionKey: 'expireclaims_filter',
        defaultOrderBy: 'fk_applicant_id',
        defaultSortBy: 'desc',
        search: true,
        dateFilter: { label: 'Expire Date', fromKey: 'fromDt', toKey: 'toDt' },
        selectFilters: [
          {
            kind: 'lockStatus',
            key: 'lock_status',
            label: 'Lock Status',
            placeholder: 'Lock Status',
          },
        ],
        columns: expireDefaultsColumns,
      }}
    />
  )
}

export function ActivePropertiesNoApplicationReportPage() {
  return (
    <ConfiguredReportShell
      config={{
        title: 'Active Properties No Application Report',
        testIdPrefix: 'active-properties-no-application',
        routePath: 'active-properties-no-application',
        apiPath: '/api/property_no_apps',
        exportPath: '/api/export/property_no_apps',
        sessionKey: 'activepropnoapps_filter',
        defaultOrderBy: 'property_name',
        search: true,
        dateFilter: {
          label: 'Date Range',
          fromKey: 'fromDt',
          toKey: 'toDt',
          defaultToCurrentMonth: true,
        },
        selectFilters: [
          ...COMPANY_PROPERTY_SALES_FILTERS,
          {
            kind: 'propertyStatus',
            key: 'status',
            label: 'Property Status',
            placeholder: 'Property Status',
          },
        ],
        initialValues: { status: '1' },
        columns: activePropertiesColumns,
      }}
    />
  )
}

export function ChargebackReportPage() {
  return (
    <ConfiguredReportShell
      config={{
        title: 'Chargeback Report',
        testIdPrefix: 'chargeback',
        routePath: 'chargeback',
        apiPath: '/api/payment_adjustment',
        exportPath: '/api/export/payment_adjustment_chargeback',
        sessionKey: 'chargeback_filter',
        defaultOrderBy: 'created_at',
        defaultSortBy: 'desc',
        search: true,
        dateFilter: { label: 'Date Range', fromKey: 'fromDt', toKey: 'toDt' },
        selectFilters: [
          {
            kind: 'company',
            key: 'company_id',
            label: 'Company',
            placeholder: 'All Company',
          },
          {
            kind: 'property',
            key: 'property_id',
            label: 'Property',
            placeholder: 'All Property',
          },
          {
            kind: 'source',
            key: 'source',
            label: 'Payment Source',
            placeholder: 'Payment Source',
          },
          {
            kind: 'chargebackStatus',
            key: 'chargeback_status',
            label: 'Chargeback Status',
            placeholder: 'Chargeback Status',
          },
        ],
        columns: chargebackColumns,
        tableClassName: reportStyles.chargebackListingTable,
        columnCustomization: {
          storageKey: CHARGEBACK_REPORT_COLUMN_VISIBILITY_STORAGE_KEY,
          lockedColumnIds: [...CHARGEBACK_REPORT_LOCKED_COLUMN_IDS],
          trailingColumnIds: [...CHARGEBACK_REPORT_TRAILING_COLUMN_IDS],
        },
      }}
    />
  )
}

export function MissingContactsReportPage() {
  return (
    <ConfiguredReportShell
      config={{
        title: 'Missing Contacts Report',
        testIdPrefix: 'missing-contacts',
        routePath: 'missing-contacts',
        apiPath: '/api/property_missing_role',
        exportPath: '/api/export/property_missing_role',
        sessionKey: 'missingcontacts_filter',
        defaultOrderBy: 'company_name',
        defaultSortBy: 'asc',
        search: true,
        selectFilters: [
          {
            kind: 'pmc',
            key: 'pmc',
            label: 'Report Type',
            placeholder: 'Report Type',
          },
          {
            kind: 'roleName',
            key: 'role_name',
            label: 'Role',
            placeholder: 'Role',
            multiple: true,
          },
          ...COMPANY_PROPERTY_SALES_FILTERS,
          {
            kind: 'listed',
            key: 'listed',
            label: 'Liberty Listed',
            placeholder: 'Listed Status',
          },
        ],
        initialValues: {
          pmc: 'PROPERTY',
          role_name: ['ROLE_PROPERTY_MANAGER'],
          listed: '1',
        },
        columns: missingContactsColumns,
      }}
    />
  )
}

export function MobileEventLogPage() {
  return (
    <ConfiguredReportShell
      config={{
        title: 'Mobile Event Log',
        testIdPrefix: 'mobile-event-log',
        routePath: 'mobile-event-log',
        apiPath: '/api/app_event_logs',
        sessionKey: 'mobileevent_filter',
        defaultOrderBy: 'fk_applicant_id',
        defaultSortBy: 'desc',
        search: false,
        dateFilter: { label: 'Date Range', fromKey: 'fromDt', toKey: 'toDt' },
        selectFilters: [
          {
            kind: 'device',
            key: 'device',
            label: 'Device',
            placeholder: 'Device',
          },
          {
            kind: 'actionKey',
            key: 'action_key',
            label: 'Action',
            placeholder: 'Action',
          },
        ],
        columns: mobileEventColumns,
        footer: (_rows, response) => {
          const statistic = response?.statistic as
            | Record<string, unknown>
            | undefined
          if (!statistic) return null
          return (
            <tr className="bg-slate-50 font-semibold text-slate-700">
              <td colSpan={mobileEventColumns.length} className="px-3 py-3">
                Registrations: {display(statistic.registrations)} | App Filled:{' '}
                {display(statistic.form_completed)} | App Fee:{' '}
                {display(statistic.app_fees)} | Access Fee:{' '}
                {display(statistic.access_fees)}
              </td>
            </tr>
          )
        },
      }}
    />
  )
}

export function FailedLoginsReportPage() {
  return (
    <ConfiguredReportShell
      config={{
        title: 'Failed Logins Log',
        testIdPrefix: 'failed-logins',
        routePath: 'failed-logins',
        apiPath: '/api/failed_login',
        sessionKey: 'failed_logins_logs_filter',
        defaultOrderBy: 'created_at',
        defaultSortBy: 'desc',
        search: true,
        dateFilter: { label: 'Date Range', fromKey: 'fromDt', toKey: 'toDt' },
        selectFilters: [
          {
            kind: 'failedLoginType',
            key: 'type',
            label: 'Type',
            placeholder: 'Login Failure Type',
          },
        ],
        columns: failedLoginsColumns,
      }}
    />
  )
}

export function DailyApplicationReportPage() {
  return (
    <ConfiguredReportShell
      config={{
        title: 'Daily Application Report',
        testIdPrefix: 'daily-application',
        routePath: 'daily-application',
        apiPath: '/api/daily_approved_application',
        exportPath: '/api/export/daily_approved_application',
        sessionKey: 'daily_application_filter',
        defaultOrderBy: 'application_completed',
        defaultSortBy: 'desc',
        search: true,
        dateFilter: {
          label: 'Application Completed',
          fromKey: 'fromDt',
          toKey: 'toDt',
          defaultToToday: true,
          apiFormat: 'YMD',
          exportFormat: 'DMY',
        },
        notice:
          'Note: This report follows Angular and may not match dashboard counts because dashboard metrics use additional status rules.',
        selectFilters: [
          {
            kind: 'company',
            key: 'company_id',
            label: 'Company',
            placeholder: 'All Company',
          },
          {
            kind: 'property',
            key: 'property_id',
            label: 'Property',
            placeholder: 'All Property',
          },
        ],
        columns: dailyApplicationColumns,
      }}
    />
  )
}

function LossRatioReportPage({ subtype }: { subtype: 'CONTRACT' | 'MRG' }) {
  const isMrg = subtype === 'MRG'
  return (
    <ConfiguredReportShell
      config={{
        title: isMrg ? 'MRG Loss Ratio' : 'Contract Loss Ratio',
        testIdPrefix: isMrg ? 'mrg-loss-ratio' : 'contract-loss-ratio',
        routePath: isMrg ? 'mrg-loss-ratio' : 'contract-loss-ratio',
        apiPath: '/api/pmc_loss_ratio',
        exportPath: '/api/export/pmc_loss_ratio',
        sessionKey: 'lossratio_filter',
        defaultOrderBy: 'company_name',
        search: false,
        dateFilter: {
          label: 'Date Range',
          fromKey: 'fromDt',
          toKey: 'toDt',
          defaultToYearStart: true,
          apiFormat: 'YMD',
          exportFormat: 'DMY',
        },
        selectFilters: [
          {
            kind: 'reportType',
            key: 'report_type',
            label: 'Report Type',
            placeholder: 'Report Type',
          },
          {
            kind: 'company',
            key: 'company_id',
            label: 'Company',
            placeholder: 'All Company',
          },
          {
            kind: 'property',
            key: 'property_id',
            label: 'Property',
            placeholder: 'All Property',
          },
          {
            kind: 'listed',
            key: 'listed',
            label: 'Liberty Listed',
            placeholder: 'Listed Status',
          },
        ],
        textFilters: [
          {
            key: 'vintage_year',
            label: 'Vintage Year',
            placeholder: 'Vintage Year',
          },
        ],
        initialValues: { report_subtype: subtype, report_type: 'COMPANY' },
        lockedValues: { report_subtype: subtype },
        columns: lossRatioColumns(
          isMrg ? 'MRG Premium' : 'Contracts Amount',
          isMrg ? 'mrg_premium' : 'contract_revenue',
        ),
      }}
    />
  )
}

export function ContractLossRatioReportPage() {
  return <LossRatioReportPage subtype="CONTRACT" />
}

export function MrgLossRatioReportPage() {
  return <LossRatioReportPage subtype="MRG" />
}

export function W9UploadedReportPage() {
  return (
    <ConfiguredReportShell
      config={{
        title: 'W9 Uploaded Report',
        testIdPrefix: 'w9-uploaded',
        routePath: 'w9-uploaded',
        apiPath: '/api/pmc_identification',
        exportPath: '/api/export/pmc_identification',
        sessionKey: 'w9uploaded_filter',
        defaultOrderBy: 'company_name',
        search: false,
        selectFilters: [
          {
            kind: 'reportType',
            key: 'report_type',
            label: 'Report Type',
            placeholder: 'Report Type',
          },
          {
            kind: 'company',
            key: 'company_id',
            label: 'Company',
            placeholder: 'All Company',
          },
          {
            kind: 'property',
            key: 'property_id',
            label: 'Property',
            placeholder: 'All Property',
          },
          {
            kind: 'uploaded',
            key: 'uploaded',
            label: 'Uploaded W9',
            placeholder: 'Uploaded Status',
          },
          {
            kind: 'listed',
            key: 'listed',
            label: 'Liberty Listed',
            placeholder: 'Listed Status',
          },
        ],
        initialValues: { report_type: 'COMPANY', uploaded: '1', listed: '1' },
        columns: w9Columns,
      }}
    />
  )
}

export function OrganicGrowthReportPage() {
  return (
    <PageShell
      title="Organic Growth"
      pageHeaderTestIdRoot="organic-growth-page-header"
      contentClassName="!space-y-6 w-full max-w-none min-w-0"
    >
      <div className="overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm">
        <iframe
          title="Organic Growth"
          src="https://docs.google.com/spreadsheets/d/1NZX9q-HTy4gnhexb9bOOVpgasc6HHXgO/edit?usp=sharing&rtpof=true&sd=true&widget=true&headers=false"
          className="h-[75vh] w-full"
        />
      </div>
    </PageShell>
  )
}

export function ApprovedNotPurchasedReportPage() {
  return (
    <ConfiguredReportShell
      config={{
        title: 'Approved Not Purchased',
        testIdPrefix: 'approved-not-purchased',
        routePath: 'approved-not-purchased',
        apiPath: '/api/approved_no_policy',
        exportPath: '/api/export/approved_no_policy',
        sessionKey: 'approved_not_purchased_filter',
        defaultOrderBy: 'lease_start_date',
        defaultSortBy: 'desc',
        search: true,
        dateFilter: {
          label: 'Lease Start Date',
          fromKey: 'fromDt',
          toKey: 'toDt',
          apiFormat: 'YMD',
          exportFormat: 'DMY',
        },
        selectFilters: [
          {
            kind: 'company',
            key: 'company_id',
            label: 'Company',
            placeholder: 'All Company',
          },
          {
            kind: 'property',
            key: 'property_id',
            label: 'Property',
            placeholder: 'All Property',
          },
        ],
        columns: approvedNotPurchasedColumns,
      }}
    />
  )
}

export function ApplicationCompletedFeeNotPaidReportPage() {
  return (
    <ConfiguredReportShell
      config={{
        title: 'Application Completed Fee Not Paid',
        testIdPrefix: 'application-completed-fee-not-paid',
        routePath: 'application-completed-fee-not-paid',
        apiPath: '/api/application_fee_not_paid',
        exportPath: '/api/export/application_fee_not_paid',
        sessionKey: 'feenotpaid_filter',
        defaultOrderBy: 'applicant_id',
        defaultSortBy: 'desc',
        search: false,
        columns: feeNotPaidColumns,
      }}
    />
  )
}

export function PropertyActivityReportPage() {
  return (
    <ConfiguredReportShell
      config={{
        title: 'Property Activity',
        testIdPrefix: 'property-activity',
        routePath: 'property-activity',
        apiPath: '/api/property_active_user',
        exportPath: '/api/export/property_active_user',
        sessionKey: 'property_activity_filter',
        defaultOrderBy: 'company_name',
        search: false,
        selectFilters: [
          {
            kind: 'reportType',
            key: 'report_type',
            label: 'Report Type',
            placeholder: 'Report Type',
          },
          {
            kind: 'company',
            key: 'company_id',
            label: 'Company',
            placeholder: 'All Company',
          },
          {
            kind: 'property',
            key: 'property_id',
            label: 'Property',
            placeholder: 'All Property',
          },
          {
            kind: 'listed',
            key: 'listed',
            label: 'Liberty Listed',
            placeholder: 'Listed Status',
          },
          {
            kind: 'lastLogin',
            key: 'last_login',
            label: 'Last Login',
            placeholder: 'Last Login',
          },
        ],
        initialValues: { report_type: 'COMPANY', listed: '1' },
        columns: propertyActivityColumns,
      }}
    />
  )
}

export function LrForm16ReportPage() {
  return (
    <ConfiguredReportShell
      config={{
        title: 'LR Form 16',
        testIdPrefix: 'lr-form-16',
        routePath: 'lr-form-16',
        apiPath: '/api/denied_email_report',
        exportPath: '/api/export/denied_email_report',
        sessionKey: 'lrform16_filter',
        defaultOrderBy: 'applicant_id',
        defaultSortBy: 'desc',
        search: true,
        dateFilter: {
          label: 'Date Range',
          fromKey: 'fromDt',
          toKey: 'toDt',
          defaultToCurrentMonth: true,
        },
        columns: lrForm16Columns,
      }}
    />
  )
}

export function DynamicPricingReportPage() {
  return (
    <ConfiguredReportShell
      config={{
        title: 'Dynamic Pricing',
        testIdPrefix: 'dynamic-pricing',
        routePath: 'dynamic-pricing',
        apiPath: '/api/dynamic_pricing_report',
        exportPath: '/api/export/dynamic_pricing_report',
        sessionKey: 'dynamicpricing_filter',
        defaultOrderBy: 'applicant_id',
        defaultSortBy: 'desc',
        search: true,
        dateFilter: {
          label: 'Date Range',
          fromKey: 'fromDt',
          toKey: 'toDt',
          defaultToCurrentMonth: true,
        },
        columns: dynamicPricingColumns,
      }}
    />
  )
}
